import React, { type ComponentType } from 'react';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';
import LooksTwoOutlinedIcon from '@mui/icons-material/LooksTwoOutlined';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import type { ICampaignCategory } from '../../../components/EsForm/CampaignCategorySelector';
import type { ContestSelectOption } from './contestSelectOptions';

export type PrizePayout = {
    label: string;
    prize: number;
    additional_prize: string[];
    /** Set when editing an existing AwardCategory — save updates instead of creating duplicates. */
    _id?: string;
};

export type PrizePlacement = {
    position: string;
    payouts: PrizePayout[];
};

const AWARD_LAYOUT_META: Array<{
    value: string;
    label: string;
    subtitle: string;
    Icon: ComponentType<SvgIconProps>;
}> = [
    {
        value: 'podium_tiers',
        label: 'Podium × tiers',
        subtitle: '1st, 2nd, 3rd place — prize per submission tier',
        Icon: EmojiEventsOutlinedIcon,
    },
    {
        value: 'tier_champions',
        label: 'One winner per tier',
        subtitle: 'Gold champion, Silver champion, etc.',
        Icon: WorkspacePremiumOutlinedIcon,
    },
    {
        value: 'top_two',
        label: 'Top 2 places',
        subtitle: 'First and second only, all tiers',
        Icon: LooksTwoOutlinedIcon,
    },
];

const AWARD_BUDGET_META: Array<{
    value: string;
    label: string;
    subtitle: string;
    Icon: ComponentType<SvgIconProps>;
}> = [
    { value: 'low', label: 'Starter', subtitle: 'Smaller cash prizes', Icon: SavingsOutlinedIcon },
    { value: 'medium', label: 'Standard', subtitle: 'Balanced prize pool', Icon: SavingsOutlinedIcon },
    { value: 'high', label: 'Competitive', subtitle: 'Strong creator incentive', Icon: SavingsOutlinedIcon },
    { value: 'premium', label: 'Premium', subtitle: 'Festival-level payouts', Icon: SavingsOutlinedIcon },
];

export function getAwardLayoutSelectOptions(): ContestSelectOption[] {
    return AWARD_LAYOUT_META.map(o => ({
        value: o.value,
        label: o.label,
        subtitle: o.subtitle,
        icon: React.createElement(o.Icon),
    }));
}

export function getAwardBudgetSelectOptions(): ContestSelectOption[] {
    return AWARD_BUDGET_META.map(o => ({
        value: o.value,
        label: o.label,
        subtitle: o.subtitle,
        icon: React.createElement(o.Icon),
    }));
}

export const SUGGESTED_PLACEMENTS = ['1st Place', '2nd Place', '3rd Place', 'Runner-up', 'Honorable Mention'];

/** Non-monetary perks — certificates, gear, exposure, etc. */
export const SUGGESTED_ADDITIONAL_PRIZES = [
    'Certificate',
    'Trophy',
    'Winner badge',
    'Social feature',
    'Mentorship session',
    'Production gear',
    'Festival pass',
];

const BUDGET_MULTIPLIER: Record<string, number> = {
    low: 0.45,
    medium: 1,
    high: 1.65,
    premium: 2.75,
};

export function tierLabelsFromCategory(category: ICampaignCategory): string[] {
    const labels = (category.tier_fee || []).map(t => t.label?.trim()).filter(Boolean) as string[];
    return labels.length ? labels : ['Standard'];
}

export function emptyPayoutsForTiers(tierLabels: string[]): PrizePayout[] {
    return tierLabels.map(label => ({ label, prize: 0, additional_prize: [] }));
}

const MONGO_OBJECT_ID_RE = /^[a-f0-9]{24}$/i;

function resolvePayoutTierLabel(payout: PrizePayout, tierIndex: number, tierLabels: string[]): string {
    const raw = payout.label?.trim() || '';
    if (!raw || MONGO_OBJECT_ID_RE.test(raw)) {
        return tierLabels[tierIndex] || tierLabels[0] || 'Standard';
    }
    return raw;
}

/** Keep prize_structure payouts aligned with current tier_fee labels (by index, preserving amounts). */
export function reconcilePrizeStructureWithTiers(category: ICampaignCategory): ICampaignCategory {
    const tierLabels = tierLabelsFromCategory(category);
    const placements = category.prize_structure || [];
    if (!placements.length) {
        return category;
    }

    const syncedPlacements = placements.map(placement => {
        const oldPayouts = placement.payouts || [];
        const oldTierLabels = oldPayouts.map((p, i) => resolvePayoutTierLabel(p, i, tierLabels));

        let position = placement.position;
        for (let i = 0; i < oldTierLabels.length; i++) {
            const oldLabel = oldTierLabels[i];
            const newLabel = tierLabels[i];
            if (oldLabel && newLabel && position === `${oldLabel} Winner`) {
                position = `${newLabel} Winner`;
                break;
            }
        }

        const payouts = tierLabels.map((label, tierIndex) => {
            const byIndex = oldPayouts[tierIndex];
            const byLabel = oldPayouts.find(p => {
                const pl = p.label?.trim() || '';
                return pl && !MONGO_OBJECT_ID_RE.test(pl) && pl.toLowerCase() === label.toLowerCase();
            });
            const byOldLabel = oldPayouts.find((p, i) => {
                const pl = resolvePayoutTierLabel(p, i, tierLabels);
                return pl.toLowerCase() === label.toLowerCase();
            });
            const source = byLabel || byIndex || byOldLabel;
            return {
                label,
                prize: source?.prize ?? 0,
                additional_prize: source?.additional_prize ?? [],
                ...(source?._id ? { _id: source._id } : {}),
            };
        });

        return { position, payouts };
    });

    return { ...category, prize_structure: syncedPlacements };
}

export function generatePrizeStructure(
    category: ICampaignCategory,
    layout: string,
    budgetLevel: string,
): PrizePlacement[] {
    const tiers = tierLabelsFromCategory(category);
    const mult = BUDGET_MULTIPLIER[budgetLevel] ?? 1;
    const tierMult = (index: number) => 1 + index * 0.12;

    if (layout === 'tier_champions') {
        return tiers.map((label, i) => ({
            position: `${label} Winner`,
            payouts: [
                {
                    label,
                    prize: Math.round((1200 + i * 650) * mult * tierMult(i)),
                    additional_prize: ['Winner certificate'],
                },
            ],
        }));
    }

    const positions = layout === 'top_two' ? ['1st Place', '2nd Place'] : ['1st Place', '2nd Place', '3rd Place'];
    const baseByPlace = [5000, 2800, 1200];

    return positions.map((position, placeIndex) => ({
        position,
        payouts: tiers.map((label, tierIndex) => ({
            label,
            prize: Math.round((baseByPlace[placeIndex] ?? 800) * mult * tierMult(tierIndex)),
            additional_prize:
                placeIndex === 0 && tierIndex === tiers.length - 1
                    ? ['Trophy', 'Certificate']
                    : placeIndex === 0
                    ? ['Certificate']
                    : [],
        })),
    }));
}
